import type React from "react";
import { useLayoutEffect, useRef } from "react";
import classes from "./AnimateFlip.module.css";

export interface AnimateFlipProps {
  children: React.ReactNode;
}

/**
 * Animates its child from where it was laid out before to where it is laid
 * out now, relative to its parent.
 */
export function AnimateFlip(props: AnimateFlipProps) {
  const { children } = props;
  const elementRef = useRef<HTMLDivElement>(null);
  const previousPos = useRef<Position | null>(null);

  useLayoutEffect(() => {
    const element = elementRef.current;
    const child = element?.firstElementChild;

    /*
     * Forgetting the position of a child that is missing or hidden - e.g. in
     * a closed drawer - keeps it from flying in from where it was last seen
     * once it shows up again.
     */
    if (!element || !child?.checkVisibility({ visibilityProperty: true })) {
      previousPos.current = null;
      return;
    }

    const childRect = child.getBoundingClientRect();
    const parentRect = getLayoutParent(element)?.getBoundingClientRect();
    const top = childRect.top - (parentRect?.top ?? 0);
    const left = childRect.left - (parentRect?.left ?? 0);
    const previous = previousPos.current;
    previousPos.current = { top, left };

    if (!previous) {
      return;
    }

    const dx = previous.left - left;
    const dy = previous.top - top;

    if (dx === 0 && dy === 0) {
      return;
    }

    child.animate(
      [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }],
      { duration: 195, easing: "cubic-bezier(0.4, 0, 0.6, 1)" },
    );
  });

  return (
    <div className={classes.root} ref={elementRef}>
      {children}
    </div>
  );
}

interface Position {
  top: number;
  left: number;
}

/**
 * The closest ancestor that generates a box.
 * Elements with `display: contents` have none to measure against.
 */
function getLayoutParent(element: Element) {
  let parent = element.parentElement;

  while (parent && getComputedStyle(parent).display === "contents") {
    parent = parent.parentElement;
  }

  return parent;
}
